import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { singletonHook } from 'react-singleton-hook';

export interface IStatus {
  /** Optional status message to take the entire site offline. */
  APP_OFFLINE?: string;
  /** Optional status message to display as toast. */
  APP_TOAST?: string;
}

/** Delay (in milliseconds) between refreshing from the server. */
const REFRESH_TIME_MS = 5 * 60 * 1000;

/**
 * Hook to retrieve APP_OFFLINE and APP_TOAST status from Express backend.
 */
const useStatus = () => {
  /** Loading value FIRST TIME ONLY in progress. */
  const [pending, setPending] = useState(true);
  /** Current App Offline message. */
  const [appOffline, setAppOffline] = useState<string>();
  /** Current App Toast message. */
  const [appToast, setAppToast] = useState<string>();

  /** Refresh from the server. */
  const refresh = useRef<() => void>(() => {
    axios
      .get('/status')
      .then((res) => res.data)
      .then((status: IStatus) => {
        setAppOffline(status.APP_OFFLINE);
        setAppToast(status.APP_TOAST);
      })
      .catch((err) => console.error(`Failed to load status ${err}`))
      .finally(() => setPending(false));
  });

  useEffect(() => refresh.current(), []);
  useEffect(() => {
    const timer = setInterval(refresh.current, REFRESH_TIME_MS);
    return () => clearInterval(timer);
  });

  return {
    pending: pending !== false, // pending is undefined on first load (?!)
    appOffline,
    appToast
  };
};

interface IUseStatus {
  pending: boolean;
  appOffline: string | undefined;
  appToast: string | undefined;
}

const init = {} as IUseStatus;

export default singletonHook<IUseStatus>(init, useStatus);
